import { SandPhysics } from './SandPhysics';
import { WaterPhysics } from './WaterPhysics';
import { WaveGenerator } from '../water/WaveGenerator';
import type { SandGrid } from '../sandgrid/SandGrid';
import type { WaterGrid } from '../water/WaterGrid';
import type { WetSandTracker } from '../WetSandTracker';
import type { FoamParticleSystem } from '../particles/FoamParticleSystem';
import type { DustParticleSystem } from '../particles/DustParticleSystem';
import type { SimulationConfig } from '../../config/SimulationConfig';

export class PhysicsEngine {
  private sandPhysics: SandPhysics;
  private waterPhysics: WaterPhysics;
  private waveGenerator: WaveGenerator;

  constructor(config: SimulationConfig) {
    this.sandPhysics = new SandPhysics(
      config.sand.angleOfRepose,
      config.sand.collapseRate,
      config.sand.cohesion
    );

    this.waterPhysics = new WaterPhysics(
      config.water.flowSpeed,
      config.water.erosionRate
    );

    this.waveGenerator = new WaveGenerator(
      config.waves.baseTideLevel,
      config.waves.tideAmplitude,
      config.waves.tideFrequency,
      config.waves.waveAmplitude,
      config.waves.waveFrequency,
      config.waves.enabled
    );
  }

  public update(
    sandGrid: SandGrid,
    waterGrid: WaterGrid,
    wetSandTracker: WetSandTracker,
    foamParticles: FoamParticleSystem,
    dustParticles: DustParticleSystem,
    deltaTime: number,
    time: number
  ): void {
    this.waveGenerator.applyToWaterGrid(waterGrid, sandGrid, time);

    this.waterPhysics.updateFlow(waterGrid, sandGrid, deltaTime);

    this.waterPhysics.applyErosion(sandGrid, waterGrid, deltaTime);

    this.sandPhysics.updateStability(sandGrid, deltaTime, dustParticles);

    wetSandTracker.update(waterGrid, sandGrid, deltaTime);

    // Update particle systems
    foamParticles.update(deltaTime);
    foamParticles.emitFromWater(waterGrid, sandGrid, deltaTime);

    dustParticles.update(deltaTime);
  }

  public getSandPhysics(): SandPhysics {
    return this.sandPhysics;
  }

  public getWaterPhysics(): WaterPhysics {
    return this.waterPhysics;
  }

  public getWaveGenerator(): WaveGenerator {
    return this.waveGenerator;
  }
}
